import {
  ElementType,
  ElementTypes,
  RegulatoryTypeEnum,
} from "@types";
import { captureSentryMessage } from "@utils";
import { elementsWithNecessaryFunctions } from "src/config/dataKeyFunctions/question-level-functions";
import { roadmapVisibilityConditionsSchema } from "src/stores/models/roadmap-visibility-conditions";
import * as yup from "yup";
import { array, boolean, InferType, mixed, number, object, string } from "yup";

const dataKeyQuestionOptionsBaseSchema = object({
  label: string().required(),
  helperText: string().optional(),
  default: string().optional(),
});

const dataKeyQuestionOptionsTextFieldSchema =
  dataKeyQuestionOptionsBaseSchema.concat(
    object({
      type: string().oneOf(["text", "number"]).optional(),
      rows: number().optional(),
    })
  );

const dataKeyQuestionOptionsSelectSchema =
  dataKeyQuestionOptionsBaseSchema.concat(
    object({
      options: array().of(string()).optional(),
      multiple: boolean().optional(),
    })
  );

const dataKeyQuestionOptionsAnswerItemsSchema =
  dataKeyQuestionOptionsBaseSchema.concat(
    object({
      typeId: string().optional(),
      fields: array().of(
        object({
          required: boolean().optional(),
          disabled: boolean().optional(),
          readOnly: boolean().optional(),
          fieldKey: string().required(),
          label: string().required(),
          element: string().oneOf(Object.values(ElementTypes)).required(),
          style: object().optional(),
          options: array()
            .of(
              object({
                value: mixed<string | number | boolean>().required(),
                label: string().required(),
              })
            )
            .optional(),
          containerAlignment: string().oneOf(["row", "column"]).optional(),
          rows: number().optional(),
          default: string().optional(),
          alwaysShown: boolean().optional(),
          multiple: boolean().optional(),
        })
      ),
    })
  );

const dataKeyQuestionOptionsParsedFileUploadSchema =
  dataKeyQuestionOptionsBaseSchema.concat(
    object({
      allowedFileTypes: string().required(),
    })
  );
const dataKeyQuestionOptionsDateSchema =
  dataKeyQuestionOptionsBaseSchema.concat(
    object({
      hideLabel: boolean().optional(),
    })
  );

const dataKeyQuestionOptionsTableSchema =
  dataKeyQuestionOptionsBaseSchema.concat(
    object({
      columns: array()
        .of(
          object({
            name: string(),
            options: object({
              flex: number().optional(),
              type: string().optional(),
              maxWidth: number().optional(),
              valueOptions: array().of(string()).optional(),
              editable: boolean().optional(),
              multiline: boolean().optional(),
            }).optional(),
          })
        )
        .optional(),
      idColumn: string().optional(),
      addRowEnabled: boolean().optional(),
    })
  );

const dataKeyQuestionOptionsRadioButtonSchema =
  dataKeyQuestionOptionsBaseSchema.concat(
    object({
      options: array().of(
        object({
          value: mixed<string | number | boolean>().required(),
          label: string().required(),
        }).optional()
      ),
    })
  );

const dataKeyQuestionOptionsTextSchema = object({
  text: string().required(),
});

const elementTypeToSchemaMap: Record<ElementType, yup.ObjectSchema<any>> = {
  [ElementTypes.TextField]: dataKeyQuestionOptionsTextFieldSchema,
  [ElementTypes.SelectElement]: dataKeyQuestionOptionsSelectSchema,
  [ElementTypes.answerItemsElement]: dataKeyQuestionOptionsAnswerItemsSchema,
  [ElementTypes.parsedFileUploadElement]:
    dataKeyQuestionOptionsParsedFileUploadSchema,
  [ElementTypes.dateElement]: dataKeyQuestionOptionsDateSchema,
  [ElementTypes.table]: dataKeyQuestionOptionsTableSchema,
  [ElementTypes.fileUpload]: dataKeyQuestionOptionsParsedFileUploadSchema,
  [ElementTypes.RadioButton]: dataKeyQuestionOptionsRadioButtonSchema,
  [ElementTypes.Text]: dataKeyQuestionOptionsTextSchema,
};

const getTypeOptionsSchema = (elementType: ElementType) => {
  try {
    return elementTypeToSchemaMap[elementType];
  } catch (e) {
    throw new Error(`No schema found for element type: ${elementType}`);
  }
};

// "OR" logic: pass if base + any one variant passes
const dataKeyQuestionOptionsSchema = yup
  .object()
  .test(
    "dataKeyQuestionOptionsSchema",
    "Must schema from type",
    function (value, context) {
      try {
        const optionsSchema = getTypeOptionsSchema(context.parent.type);
        optionsSchema.validateSync(value, {
          stripUnknown: true,
        });
        return true;
      } catch (e) {
        return this.createError({
          message: `Does not match any schema variant. ${e}`,
        });
      }
    }
  );

const questionSchema = object({
  id: string().required(),
  type: string().oneOf(Object.values(ElementTypes)).required(),
  options: dataKeyQuestionOptionsSchema.required(),
}).test("has-necessary-functions-in-code", "", (currentQuestionValue) => {
  const elementFunctionInfo =
    elementsWithNecessaryFunctions[currentQuestionValue.type];

  // If this dataKey needs a necessary functions
  if (elementFunctionInfo) {
    // if that function is not added to our code
    // capture the error, but don't break the whole frontend
    if (
      elementFunctionInfo?.dataKeyToFunctionMap[currentQuestionValue.id] ===
      undefined
    ) {
      const errorMessage = `Data Key: "${currentQuestionValue.id || "NOT FOUND"}" of type: "${currentQuestionValue.type || "NOT FOUND"}" is missing its "${elementFunctionInfo.functionName || "NOT FOUND"}" function.`;
      console.error(errorMessage);
      captureSentryMessage(errorMessage);
    }
  }
  return true;
});

export const dataKeySchema = object({
  key: string<string>().required(),
  required: boolean().required(),
  question: questionSchema.required(),
  context: array().of(string()).optional(),
  videoUrl: string().nullable().optional(),
  visibilityConditions: roadmapVisibilityConditionsSchema.nullable().optional(),
  createdAt: string().required(),
  updatedAt: string().required(),
});

const dataKeyArrayItemSchema = object({
  order: number().nullable(),
  documentTemplateId: string().required(),
  dataKey: dataKeySchema.required(),
});

export type DataKeyQuestion = InferType<typeof questionSchema>;
export type DataKey = InferType<typeof dataKeySchema>;
export type DataKeyItem = InferType<typeof dataKeyArrayItemSchema>;

export const documentTemplateSchema = object({
  id: string().required(),
  hidden: boolean().required(),
  deviceId: string().nullable(),
  templateKey: string<string>().required(),
  organizationId: string().nullable(),
  functionalGroupAcronym: string().nullable(),
  regulatoryType: mixed<RegulatoryTypeEnum>()
    .oneOf(Object.values(RegulatoryTypeEnum))
    .nullable(),
  defaultTitle: string().required(),
  multiDocStrategy: string<"newDocument">().nullable(),
  userUpload: boolean().required().optional(),
  isOrgDocument: boolean().required().optional(),
  dependencies: array().of(string<string>().required()).optional(),
  docReferences: array().of(string().required()).optional(),
  contentTemplate: string().nullable(),
  deprecatedByTemplateKey: string<string>().nullable(),
  dataKey: array().of(dataKeyArrayItemSchema).optional(),
  createdAt: string().required(),
  updatedAt: string().required(),
});

export interface DocumentTemplate
  extends InferType<typeof documentTemplateSchema> {}

export const documentTemplateSchemas = array().of(documentTemplateSchema);

export type TemplatesConfig = InferType<typeof documentTemplateSchemas>;

// The templateKeys that are used in code that need to always exists in the db
export enum HARDCODED_TEMPLATE_KEY {
  BUG_FIX_REPORT = "bug-fix-report-template",
  CHANGE_REQUEST = "change-request",
  SUBSYSTEM_REQUIREMENTS_LIST = "subsystem-requirements-list",
  SOUP_LIST = "soup-list",
  SOFTWARE_TEST_PLAN = "software-system-test-plan",
  SOFTWARE_SYSTEM_TEST_REPORT = "software-system-test-report",
  RISK_ASSESSMENT = "risk-assessment",
  DESIGN_REVIEW_CHECKLIST = "design-review-checklist",
  USABILITY_EVALUATION_PROTOCOL = "usability-evaluation-protocol",
  USABILITY_EVALUATION_REPORT = "usability-evaluation-report",
  CHANGE_EVALUATION_REPORT = "change-evaluation-report",
  LIST_OF_KNOWN_ANOMALIES = "list-of-known-anomalies",
  UDI_CREATION = "udi-creation",
  DEVICE_LABEL = "device-label",
  LIST_OF_MEDICAL_DEVICES = "list-of-medical-devices",
  RELEASE_NOTES = "release-notes",
  PRODUCT_RELEASE_CHECKLIST = "product-release-checklist",
  QUALITY_MANUAL_POLICY_OBJECTIVES = "quality-manual-policy-objectives",
  SOP_HUMAN_RESOURCES_ADMINISTRATION = "sop-human-resources-administration",
  SOP_UPDATE_OF_REGULATIONS = "sop-update-of-regulations",
  SOP_FEEDBACK_MANAGEMENT = "sop-feedback-and-complaint-management",
  LIST_OF_QUALIFIED_SUPPLIERS = "list-of-qualified-suppliers",
  USER_UPLOAD = "user-upload",
  CLINICAL_EVALUATION_REPORT = "clinical-evaluation-report",
}
